// ============================================================================
// SWIPE TO COMPLETE — docs/design/handoff-mobile/README.md §1g and §Interactions.
//
//   "The row translates right (shown at +96 px) … Past ~35% of the width,
//    release commits."
//   "swipe follows the finger 1:1 and settles in 180 ms"
//   "Left-swipe is unassigned — do not add a destructive left-swipe."
//
// THE STATE MACHINE IS PURE AND LIVES HERE, apart from the component, for the
// one reason the README's own closing section gives: the FEEL of this gesture is
// the thing no gate can photograph. What a gate CAN check is the arithmetic —
// that the row tracks the finger 1:1 rather than easing, that 34% releases back
// and 36% commits, that a leftward drag produces exactly zero movement. Those are
// the assertions in mobile.test.ts, and they are only assertable because none of
// this touches the DOM.
//
// 1:1 IS NOT A DETAIL. `offset === dx` — no easing curve, no rubber band, no
// multiplier. A row that moves 0.7× the finger reads as lag on a phone, and lag
// on a direct-manipulation gesture reads as a broken app. The ONLY shaping is the
// clamp: leftward is pinned at 0 (nothing is assigned to it) and rightward is
// pinned at the row's own width (a row cannot leave its card).
// ============================================================================

/** How far across the row the finger must get before release completes the task. */
export const COMMIT_FRACTION = 0.35

/** How long the row takes to settle back, or through, after release. */
export const SETTLE_MS = 180

/** How long the undo affordance stands after a completion. README §1g: 4 seconds. */
export const UNDO_MS = 4000

export interface SwipeState {
  /** The row under the finger, or null when nothing is being dragged. */
  rowId: string | null
  /** How far right the row has moved, in px. Never negative. */
  offset: number
}

export const NO_SWIPE: SwipeState = { rowId: null, offset: 0 }

export function beginSwipe(rowId: string): SwipeState {
  return { rowId, offset: 0 }
}

/**
 * The finger moved `dx` px from where it went down. 1:1, clamped to [0, width].
 *
 * A `dx` for a row that is not the one being dragged is ignored rather than
 * applied: two fingers on two rows is a gesture nobody designed, and picking one
 * arbitrarily would move a row the owner is not touching.
 */
export function moveSwipe(state: SwipeState, rowId: string, dx: number, width: number): SwipeState {
  if (state.rowId !== rowId) return state
  return { rowId, offset: Math.min(Math.max(dx, 0), Math.max(width, 0)) }
}

/** Has the row passed the threshold? Exactly at 35% is not past it. */
export function shouldCommit(state: SwipeState, width: number): boolean {
  if (state.rowId === null || width <= 0) return false
  return state.offset > width * COMMIT_FRACTION
}

/**
 * The finger came up.
 *
 * Returns what to do and the state that follows. `offset` goes back to 0 in BOTH
 * cases — a committed row is removed from the lens by the completion itself, and
 * leaving it parked at +96px would mean the row that is no longer there is still
 * holding a position.
 */
export function releaseSwipe(
  state: SwipeState,
  width: number,
): { commit: boolean; rowId: string | null; next: SwipeState } {
  const commit = shouldCommit(state, width)
  return { commit, rowId: state.rowId, next: NO_SWIPE }
}
